import { createSelector } from '@ngrx/store';
import {
  PaymentRuleStore,
  PayRuleStore,
} from '../reducers/payment-rules.reducer';
import {
  selectCreditCardMerchantAccounts,
  selectOriginatingApplication,
  selectVirtualOtherwisePrimaryTenantId,
} from '../../session-store/selectors/session.selectors';
import {
  GetPaymentRulePayload,
  HostedPaymentNotesType,
} from '@arc/unified-payments-frontend/shared-payment';

export const selectSurchargeSettings = createSelector(
  PaymentRuleStore,
  (state) => state?.settings?.surcharge,
);

export const selectAllowSurcharge = createSelector(
  selectSurchargeSettings,
  (state) => state?.allowSurcharging,
);

export const selectZeroDollarPaymentsEnabled = createSelector(
  PaymentRuleStore,
  (state) => state?.settings.generalPaymentSettings.zeroDollarPaymentsEnabled,
);

/* istanbul ignore next */
export const selectBlockDuplicatePaymentsEnabled = createSelector(
  PaymentRuleStore,
  (state) => state?.settings.generalPaymentSettings.blockDuplicatePayments,
);

export const selectAllowACH = createSelector(
  PaymentRuleStore,
  (state) => state?.settings.payments?.acceptBank,
);

export const selectAllowDisputeReasons = createSelector(
  PaymentRuleStore,
  (state) =>
    state?.settings?.shortAndOverPaySettings?.allowShortPay &&
    state?.settings?.shortAndOverPaySettings?.allowDisputeReason,
);

export const selectAllowCreditCard = createSelector(
  PaymentRuleStore,
  (state) => state?.settings.payments.acceptCreditCard,
);

export const selectAllowableCardBrands = createSelector(
  PaymentRuleStore,
  (state) => state?.settings.payments.allowableCardBrands,
);

export const selectCreateSavedPaymentMethod = createSelector(
  PaymentRuleStore,
  (state) => state.settings.cardOnFile?.createSavedPaymentMethod,
);

export const selectPaymentRemittanceRules = createSelector(
  PayRuleStore,
  (state) => state.paymentRemittanceRules,
);

export const selectIsRemitUploadEnabled = createSelector(
  selectPaymentRemittanceRules,
  (paymentRemittanceRules) => {
    const achEmails = paymentRemittanceRules?.achEmails;
    const creditCardEmails = paymentRemittanceRules?.creditCardEmails;

    return achEmails?.length > 0 && creditCardEmails?.length > 0;
  },
);

export const selectShowContactInformation = createSelector(
  PaymentRuleStore,
  (state) =>
    state?.settings?.contactInfoSettings?.includeContactInfo ||
    state?.settings?.paymentLinksSettings?.includeContactInfo,
);

export const selectContactInformation = createSelector(
  PaymentRuleStore,
  (state) =>
    state?.settings?.contactInfoSettings?.contactInfoMessage ||
    state?.settings?.paymentLinksSettings?.contactInfoMessage,
);

export const selectAllowBuyerLogin = createSelector(
  PaymentRuleStore,
  (state) => state?.settings?.paymentLinksSettings?.allowBuyerLogin ?? true,
);

export const selectGetPaymentRulesPayload = createSelector(
  selectVirtualOtherwisePrimaryTenantId,
  selectOriginatingApplication,
  selectCreditCardMerchantAccounts,
  (
    tenantId,
    originApplication,
    creditCardMerchantAccounts,
  ): GetPaymentRulePayload => ({
    tenantId,
    originApplication,
    cardMarsToken: creditCardMerchantAccounts[0],
  }),
);

export const selectGracePeriodSettings = createSelector(
  PaymentRuleStore,
  (state) => state?.settings?.gracePeriodSettings,
);

export const selectIsGracePeriodOn = createSelector(
  selectOriginatingApplication,
  selectGracePeriodSettings,
  (originApplication, gracePeriodSettings) => {
    const applicationSettings = gracePeriodSettings?.applications?.find(
      (app) => app.originApplication === originApplication.toUpperCase(),
    );
    return applicationSettings ? applicationSettings.enabled : false;
  },
);

export const selectFlexibleTemplateSettings = createSelector(
  PaymentRuleStore,
  (state) => state?.settings.flexibleTemplateSettings,
);

export const selectAllowFlexibleTemplates = createSelector(
  selectFlexibleTemplateSettings,
  (flexibleTemplateRules) =>
    flexibleTemplateRules?.restrictionType === 'FlexibleTemplateRestriction' &&
    flexibleTemplateRules.allowFlexibleTemplate,
);

export const selectHostedPaymentsSettings = createSelector(
  PaymentRuleStore,
  (state) => state?.settings.hostedPaymentPageSettings,
);

export const selectAccountNumberLabel = createSelector(
  selectHostedPaymentsSettings,
  (state) => state?.buyerCustomIdentifierFieldLabel,
);

export const selectDropdownSettings = createSelector(
  selectHostedPaymentsSettings,
  (state) =>
    state?.hostedPaymentNotes.find(
      (note) => note.type === HostedPaymentNotesType.FixedNote,
    ),
);

export const selectPaymentNoteSettings = createSelector(
  selectHostedPaymentsSettings,
  (state) =>
    state?.hostedPaymentNotes.find(
      (note) => note.type === HostedPaymentNotesType.FreeTextNote,
    ),
);

export const selectCvvCardOnFileSettings = createSelector(
  PaymentRuleStore,
  (state) => state?.settings.cvvCardOnFileSettings,
);

export const selectIsCvvRequireRule = createSelector(
  selectOriginatingApplication,
  selectCvvCardOnFileSettings,
  (originApplication, cvvSettings) => {
    const applicationSettings = cvvSettings?.applications?.find(
      (app) => app.originApplication === originApplication.toUpperCase(),
    );

    return applicationSettings
      ? applicationSettings.requireCvvCardOnFile
      : false;
  },
);

export const selectIsCaptchaRequired = createSelector(
  selectHostedPaymentsSettings,
  (state) => state?.captchaEnabled,
);

export const shortAndOverPaySettings = createSelector(
  PaymentRuleStore,
  (state) => state?.settings.shortAndOverPaySettings,
);

export const selectAllowOverPay = createSelector(
  shortAndOverPaySettings,
  (overPaySettings) => {
    return overPaySettings.allowOverPay ?? false;
  },
);

/* istanbul ignore next */
export const selectAllowShortPay = createSelector(
  shortAndOverPaySettings,
  (overPaySettings) => {
    return overPaySettings.allowShortPay ?? false;
  },
);

export const selectAllowStatementPaymentsSettings = createSelector(
  PaymentRuleStore,
  (state) => state?.settings.statementPaymentsSettings,
);

export const selectAllowStatementPaymentsRule = createSelector(
  selectOriginatingApplication,
  selectAllowStatementPaymentsSettings,
  (originApplication, cvvSettings) => {
    const applicationSettings = cvvSettings?.applications?.find(
      (app) => app.originApplication === originApplication.toUpperCase(),
    );

    return applicationSettings
      ? applicationSettings.allowStatementPayments
      : false;
  },
);

export const selectCustomTemplatesSettings = createSelector(
  PaymentRuleStore,
  (state) => state?.settings?.customTemplatesSettings,
);
